/**
 * Iter 245 — Configuration Shopify (Private App).
 *
 * 3 étapes : créer Custom App Shopify → coller token → tester → sync.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import {
  ShoppingCart, Loader2, CheckCircle2, AlertCircle, Copy, RefreshCw, Trash2, KeyRound,
} from "lucide-react";
import { toast } from "sonner";

export default function ShopifyConfigPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/shopify/config");
      setCfg(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/inventory/shopify/test");
      if (r.data.ok) {
        toast.success(`✓ Connexion OK — ${r.data.shop_name} (${r.data.currency})`);
      } else {
        toast.error(`Échec : ${r.data.error || r.data.status_code}`);
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setTesting(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/inventory/shopify/sync-products");
      toast.success(`Sync terminée : ${r.data.created} créés, ${r.data.updated} mis à jour${r.data.skipped_quota_30 ? `, ${r.data.skipped_quota_30} ignorés (limite 30)` : ""}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur sync"); } finally { setSyncing(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter Shopify ? La synchro sera désactivée.")) return;
    try {
      await api.delete("/inventory/shopify/config");
      toast.success("Déconnecté");
      await load();
    } catch (e) { toast.error("Erreur"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  const webhookFullUrl = `${window.location.origin}/api/inventory/shopify/webhook`;

  return (
    <div className="p-6 max-w-[900px] mx-auto space-y-5" data-testid="shopify-config-page">
      <PageHeader
        icon={<ShoppingCart />}
        title="Intégration Shopify"
        description="Connectez votre boutique Shopify pour sync produits + import auto des commandes."
      />

      {/* Status */}
      <div className={`rounded-xl p-4 border ${cfg?.configured ? "bg-emerald-500/5 border-emerald-500/30" : "bg-amber-500/5 border-amber-500/30"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {cfg?.configured ? (
              <><CheckCircle2 className="w-5 h-5 text-emerald-400" /><div><div className="text-white font-bold">Connecté à {cfg.shop_url}</div><div className="text-xs text-slate-400">Token : {cfg.access_token_masked}</div></div></>
            ) : (
              <><AlertCircle className="w-5 h-5 text-amber-400" /><div><div className="text-white font-bold">Non configuré</div><div className="text-xs text-slate-400">Suivez la procédure ci-dessous pour activer l'intégration.</div></div></>
            )}
          </div>
          {cfg?.configured && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={test} disabled={testing} data-testid="btn-test">
                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} Tester
              </Button>
              <Button size="sm" variant="outline" onClick={sync} disabled={syncing} data-testid="btn-sync">
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Sync produits"}
              </Button>
              <Button size="sm" variant="outline" onClick={disconnect} className="text-red-400" data-testid="btn-disconnect">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Procédure d'activation */}
      {!cfg?.configured && cfg?.activation && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 space-y-3" data-testid="activation-steps">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Procédure d'activation (5 étapes)</h2>
          {Object.entries(cfg.activation).filter(([k]) => k.startsWith("step_")).map(([k, v]) => (
            <div key={k} className="flex gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-[#f7c935] text-[#14101c] flex items-center justify-center text-xs font-bold flex-shrink-0">
                {k.split("_")[1]}
              </div>
              <div className="text-slate-300">{v}</div>
            </div>
          ))}
          <div className="text-xs text-slate-500 pl-9">
            <strong className="text-slate-400">Scopes requis :</strong> {cfg.activation.step_3_scopes?.join(", ")}
          </div>
        </div>
      )}

      {/* Config — centralisée dans le Hub API */}
      <div className="bg-gradient-to-br from-[#f7c935]/10 to-transparent border border-[#f7c935]/25 rounded-xl p-5" data-testid="shopify-hub-redirect">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-1">{cfg?.configured ? "Mettre à jour les clés" : "Configuration des clés"}</h2>
        <p className="text-xs text-slate-400 mb-3">
          La boutique, l'access token et le secret webhook se configurent désormais dans le Hub API (carte Shopify).
        </p>
        <Button onClick={() => navigate("/admin/integrations-health")} className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c] font-bold" data-testid="shopify-goto-hub-btn">
          <KeyRound className="w-4 h-4 mr-2" /> Ouvrir le Hub API
        </Button>
      </div>

      {/* Webhook URL */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-2">URL Webhook (à configurer dans Shopify)</h2>
        <p className="text-xs text-slate-400 mb-3">
          Dans Shopify Admin → Settings → Notifications → Webhooks → "Create webhook" : choisissez l'événement <strong>Order creation</strong>, format JSON, et collez l'URL ci-dessous.
        </p>
        <div className="flex items-center gap-2 bg-[#0b0811] border border-white/10 rounded px-3 py-2 font-mono text-xs">
          <code className="flex-1 break-all text-emerald-400">{webhookFullUrl}</code>
          <Button size="icon" variant="ghost" onClick={() => copy(webhookFullUrl)} data-testid="btn-copy-webhook">
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {cfg?.configured && (
        <div className="text-xs text-slate-500 text-center">
          {cfg.last_test_at && <>Dernier test : {new Date(cfg.last_test_at).toLocaleString()} {cfg.last_test_ok ? "✓" : "✗"}</>}
          {cfg.last_sync_at && <> · Dernière synchro : {new Date(cfg.last_sync_at).toLocaleString()} ({cfg.products_synced || 0} produits)</>}
        </div>
      )}
    </div>
  );
}
